// İSG uzmanı portalı — parola sıfırlama 2. adım. Kod + yeni parola →
// `/auth/reset-password`. Parola politikası FE'de doğrulanır (min 8 + harf + rakam,
// api-contract §5.8.2); backend `PASSWORD_POLICY_ERROR`/`CODE_*` hataları gösterilir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  resetPassword,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { z } from 'zod';

interface ResetLocationState {
  emailOrPhone?: string;
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const prefill = (location.state as ResetLocationState | null)?.emailOrPhone ?? '';

  const policyMessage = t('validation.passwordPolicy');
  const schema = useMemo(
    () =>
      z.object({
        emailOrPhone: z.string().min(1, t('validation.identifierRequired')),
        code: z.string().min(1, t('validation.codeRequired')),
        newPassword: z
          .string()
          .min(8, policyMessage)
          .regex(/[A-Za-z]/, policyMessage)
          .regex(/\d/, policyMessage),
      }),
    [t, policyMessage],
  );
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { emailOrPhone: prefill, code: '', newPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => resetPassword(values),
  });

  if (mutation.isSuccess) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6 text-success" aria-hidden="true" />
            <CardTitle as="h1">{t('reset.successTitle')}</CardTitle>
          </div>
          <CardDescription>{t('reset.successBody')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="w-full">
            <Link to="/login">{t('reset.toLogin')}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">{t('reset.title')}</CardTitle>
        <CardDescription>{t('reset.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((values) => mutation.mutate(values))}
          noValidate
        >
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          <Field
            htmlFor="emailOrPhone"
            label={t('forgot.identifier')}
            error={errors.emailOrPhone?.message}
            required
          >
            <Input
              id="emailOrPhone"
              autoComplete="username"
              invalid={Boolean(errors.emailOrPhone)}
              {...register('emailOrPhone')}
            />
          </Field>

          <Field htmlFor="code" label={t('reset.code')} error={errors.code?.message} required>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              invalid={Boolean(errors.code)}
              {...register('code')}
            />
          </Field>

          <Field
            htmlFor="newPassword"
            label={t('reset.newPassword')}
            hint={t('reset.passwordHint')}
            error={errors.newPassword?.message}
            required
          >
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              invalid={Boolean(errors.newPassword)}
              {...register('newPassword')}
            />
          </Field>

          <Button type="submit" loading={mutation.isPending} className="mt-1 w-full">
            {t('reset.submit')}
          </Button>

          <div className="text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">
              {t('forgot.backToLogin')}
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
